import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ErrorFilterService {
  // Patrones de errores que son operaciones normales del ciclo de vida
  private readonly ignoredErrorPatterns: string[] = [
    // Errores de serialización de protobuf cuando el componente se destruye
    'Assertion failed',

    // Errores de cancelación normales cuando el usuario navega
    'AbortError',
    'signal is aborted without reason',
    'Fetch is aborted',
    'Fetch error POST',
    'Request aborted',
    'Navigation cancelled',
    'The operation was aborted',

    // Errores de View Transitions API (navegación con transiciones)
    'View transition was skipped',
    'Old view transition aborted by new view transition',

    // Errores de network (conexión perdida, timeout de red, etc.)
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource',
    'net::ERR_',
    'Network request failed',

    // Errores de timeout normales
    'DEADLINE_EXCEEDED',
    'CANCELLED',
    'ABORTED',

    // Errores de Angular conocidos (no críticos)
    'NG0750', // Error de view transition en Angular

    // Otros errores conocidos de librerías externas
    '_avast_submit', // Extensión Avast
    'Non-Error promise rejection captured', // Promesas rechazadas sin error real
  ];

  // Patrones de stack trace que indican operaciones normales
  // NOTA: Estos se buscan en el STACK TRACE, no en el mensaje del error
  private readonly ignoredStackPatterns: string[] = [
    'serializeBinaryToWriter', // Serialización protobuf cuando el componente se destruye
    '.unsubscribe', // Unsubscribe de observables (cancelación normal)
    // NO incluimos 'grpc-web' ni 'frameRequest' porque podrían ocultar errores reales del servidor
  ];

  // Métricas opcionales (para debugging sin reportar)
  private ignoredErrorCounts: Map<string, number> = new Map();

  /**
   * Determina si un error debe ser ignorado (no reportado a servicios de monitoreo)
   * @param error El error a evaluar - puede ser un Error nativo, un evento de DataDog, o cualquier estructura
   * @returns true si el error debe ser ignorado, false si debe reportarse
   */
  shouldIgnoreError(error: any): boolean {
    if (!error) {
      return false;
    }

    // Detectar si es un evento de DataDog RUM
    if (this.isDataDogEvent(error)) {
      return this.shouldIgnoreDataDogEvent(error);
    }

    // Para errores tradicionales (Error objects, strings, etc.)
    const errorMessage = this.getErrorMessage(error);
    const errorStack = this.getErrorStack(error);

    // Verificar si el mensaje contiene algún patrón a ignorar
    if (errorMessage) {
      for (const pattern of this.ignoredErrorPatterns) {
        if (errorMessage.includes(pattern)) {
          this.logIgnoredError(pattern);
          return true;
        }
      }
    }

    // Verificar si el stack trace contiene patrones a ignorar
    if (errorStack) {
      for (const pattern of this.ignoredStackPatterns) {
        if (errorStack.includes(pattern)) {
          this.logIgnoredError(`stack:${pattern}`);
          return true;
        }
      }
    }

    // Verificar si es un error gRPC de cancelación
    if (this.isGrpcCancellationError(error)) {
      this.logIgnoredError('grpc-cancellation');
      return true;
    }

    // Verificar si es un error de navegación Angular
    if (this.isNavigationError(error)) {
      this.logIgnoredError('navigation-error');
      return true;
    }

    // No ignorar el error - debe ser reportado
    return false;
  }

  /**
   * Detecta si el objeto es un evento de DataDog RUM
   */
  private isDataDogEvent(obj: any): boolean {
    return obj && typeof obj === 'object' &&
           (('type' in obj && (obj.type === 'error' || obj.type === 'resource' || obj.type === 'action' || obj.type === 'view')) ||
            ('origin' in obj && obj.origin === 'network'));
  }

  /**
   * Procesa eventos de DataDog RUM para determinar si deben ser ignorados
   *
   * IMPORTANTE: Datadog RUM puede enviar eventos en MÚLTIPLES estructuras diferentes:
   * - Estructura 1: { type: 'error', error: { message, stack } }
   * - Estructura 2: { origin: 'network', http: {...}, error: { message } }
   * - Estructura 3: { type: 'resource', resource: {...} }
   *
   * Por eso verificamos el mensaje de error PRIMERO, antes de cualquier lógica
   * específica de estructura, para asegurar que capturamos AbortErrors sin importar
   * cómo Datadog estructure el evento.
   */
  private shouldIgnoreDataDogEvent(event: any): boolean {
    // ========================================
    // PRIORIDAD 1: Verificar mensaje de error PRIMERO (sin importar la estructura)
    // Este es el FIX crítico para capturar AbortError en TODAS las estructuras
    // ========================================
    const errorMessage =
      event?.error?.message ||
      event?.message ||
      '';

    if (errorMessage) {
      for (const pattern of this.ignoredErrorPatterns) {
        if (errorMessage.includes(pattern)) {
          this.logIgnoredError(`datadog-message:${pattern}`);
          return true;
        }
      }
    }

    // ========================================
    // PRIORIDAD 2: Verificar tipo de error directamente
    // ========================================
    if (event?.error?.type === 'AbortError') {
      this.logIgnoredError('datadog-abort-type');
      return true;
    }

    // ========================================
    // PRIORIDAD 3: Verificar errores de red con status 0
    // Maneja AMBAS estructuras de eventos de red
    // ========================================

    // Detectar origen de red (puede estar en event.error.origin o event.origin)
    const isNetworkOrigin =
      event?.error?.origin === 'network' ||
      event?.origin === 'network';

    if (isNetworkOrigin) {
      // Status code puede estar en múltiples lugares
      const statusCode =
        event?.error?.resource?.status_code ||
        event?.http?.status_code ||
        null;

      // Status 0 = request abortada/cancelada (navegación, componente destruido)
      if (statusCode === 0) {
        this.logIgnoredError('datadog-network-status-0');
        return true;
      }

      // Verificar URLs de gRPC con códigos de cancelación
      const url =
        event?.error?.resource?.url ||
        event?.http?.url ||
        '';

      if (url.includes('grpc.betterplan.cl') &&
          (statusCode === 0 || statusCode === 499)) {
        this.logIgnoredError('datadog-grpc-cancelled');
        return true;
      }
    }

    // ========================================
    // PRIORIDAD 4: Estructura tradicional de error (event.type === 'error')
    // ========================================
    if (event.type === 'error' && event.error) {
      const message = event.error.message || '';
      const stack = event.error.stack || '';

      // Revisar patrones de error
      for (const pattern of this.ignoredErrorPatterns) {
        if (message.includes(pattern) || stack.includes(pattern)) {
          this.logIgnoredError(`datadog-error:${pattern}`);
          return true;
        }
      }

      // Revisar patrones de stack
      for (const pattern of this.ignoredStackPatterns) {
        if (stack.includes(pattern)) {
          this.logIgnoredError(`datadog-stack:${pattern}`);
          return true;
        }
      }
    }

    // ========================================
    // PRIORIDAD 5: Eventos de recursos (event.type === 'resource')
    // ========================================
    if (event.type === 'resource' && event.resource) {
      const statusCode = event.resource.status_code;

      // Status 0 = request abortada
      if (statusCode === 0) {
        this.logIgnoredError('datadog-resource-aborted');
        return true;
      }

      // Verificar mensaje de error en recursos si existe
      if (event.resource.error && event.resource.error.message) {
        const message = event.resource.error.message;
        for (const pattern of this.ignoredErrorPatterns) {
          if (message.includes(pattern)) {
            this.logIgnoredError(`datadog-resource:${pattern}`);
            return true;
          }
        }
      }

      // Verificar URLs de gRPC
      const url = event.resource.url || '';
      if (url.includes('grpc.betterplan.cl') &&
          (statusCode === 499 || statusCode === 0)) {
        this.logIgnoredError('datadog-grpc-cancelled');
        return true;
      }
    }

    return false;
  }

  /**
   * Obtiene el mensaje del error de forma segura
   */
  private getErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.rejection?.message) {
      return error.rejection.message;
    }
    return '';
  }

  /**
   * Obtiene el stack trace del error de forma segura
   */
  private getErrorStack(error: any): string {
    if (error?.stack) {
      return error.stack;
    }
    if (error?.error?.stack) {
      return error.error.stack;
    }
    return '';
  }

  /**
   * Verifica si es un error de cancelación gRPC
   */
  private isGrpcCancellationError(error: any): boolean {
    // Verificar por código de error gRPC
    const grpcCode = error?.code || error?.error?.code;
    if (grpcCode === 1 || grpcCode === 'CANCELLED') {
      return true;
    }

    // Verificar por status gRPC
    const status = error?.status || error?.error?.status;
    if (status === 'CANCELLED' || status === 'ABORTED') {
      return true;
    }

    return false;
  }

  /**
   * Verifica si es un error de navegación Angular
   */
  private isNavigationError(error: any): boolean {
    if (error?.name === 'NavigationCanceledError') {
      return true;
    }

    const message = this.getErrorMessage(error);
    if (message.includes('Navigation ID') && message.includes('is not equal to the current navigation')) {
      return true;
    }

    return false;
  }

  /**
   * Registra métricas de errores ignorados (para debugging)
   * No reporta a servicios externos, solo para análisis interno
   */
  private logIgnoredError(pattern: string): void {
    const count = this.ignoredErrorCounts.get(pattern) || 0;
    this.ignoredErrorCounts.set(pattern, count + 1);

    // Solo en desarrollo, log para debugging
    if (localStorage.getItem('debug-error-filter') === 'true') {
      console.debug(`[ErrorFilter] Ignored error matching: ${pattern} (count: ${count + 1})`);
    }
  }

  /**
   * Obtiene las métricas de errores ignorados (útil para debugging)
   */
  getIgnoredErrorMetrics(): Map<string, number> {
    return new Map(this.ignoredErrorCounts);
  }

  /**
   * Limpia las métricas de errores ignorados
   */
  clearMetrics(): void {
    this.ignoredErrorCounts.clear();
  }
}